import axios from 'axios';
import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;

let initialized = false;

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

function istNow(): Date {
  return new Date(Date.now() + IST_OFFSET_MS);
}

function istTimestampSec(): number {
  return Math.floor(istNow().getTime() / 1000);
}

function resolveMerchantId(): number | undefined {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u.merchant_id) return u.merchant_id;
    }
    const shopsStr = localStorage.getItem('stores');
    const shop = localStorage.getItem('shop');
    if (shopsStr && shop) {
      const stores = JSON.parse(shopsStr);
      const store = stores.find((s: any) => s.shop === shop || s.name === shop);
      if (store?.id) return store.id;
    }
    if (userStr) {
      const u = JSON.parse(userStr);
      return u.id;
    }
  } catch { /* ignore */ }
  return undefined;
}

function resolveUserId(): number | undefined {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.id;
  } catch { return undefined; }
}

function resolveEnvironment(): string {
  if (typeof window === 'undefined') return 'production';
  const host = window.location.hostname;
  if (host === 'localhost') return 'local';
  if (host.endsWith('.netsights.ai')) return 'dev';
  return 'production';
}

function persistAuthEvent(event: 'login' | 'logout', email?: string): void {
  const payload: Record<string, unknown> = {
    event_name: event,
    merchant_id: resolveMerchantId(),
    user_id: resolveUserId(),
  };

  if (email) payload.email = email;

  axios.post('/track/auth', payload).catch(() => {});
}

export function initAnalytics(): void {
  if (initialized) return;
  if (!MIXPANEL_TOKEN) {
    console.warn('[Mixpanel] Token not configured - events will not be sent.');
    return;
  }
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: import.meta.env.DEV,
    track_pageview: false,
    persistence: 'localStorage',
  });
  initialized = true;
}

export function identifyUser(userId: string | number, traits?: Record<string, unknown>): void {
  if (!initialized) return;
  mixpanel.identify(String(userId));
  if (traits) {
    mixpanel.people.set(traits);
  }
}

export function resetAnalytics(): void {
  if (!initialized) return;
  mixpanel.reset();
}

export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  mixpanel.track(event, { time: istTimestampSec(), ...properties });
  if (event === 'Login' || event === 'Logout') {
    persistAuthEvent(
      event.toLowerCase() as 'login' | 'logout',
      properties?.email as string | undefined
    );
  }
}

export function trackPageView(path: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  mixpanel.track('Page View', {
    time: istTimestampSec(),
    path,
    action: path,
    event_category: 'page_view',
    environment: resolveEnvironment(),
    ...properties,
  });
}

export function registerAnalyticsSuperProps(props: Record<string, unknown>): void {
  if (!initialized) return;
  mixpanel.register(props);
}

const HEARTBEAT_INTERVAL_MS = 60000;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

export function startHeartbeat(): void {
  if (heartbeatTimer) return;

  const send = () => {
    const merchantId = resolveMerchantId();
    if (!merchantId) return;
    axios.post('/track/heartbeat', { merchant_id: merchantId }).catch(() => {});
  };

  send();
  heartbeatTimer = setInterval(send, HEARTBEAT_INTERVAL_MS);
}

export function stopHeartbeat(): void {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

export function trackAction(page: string, action: string, label?: string): void {
  if (!initialized) return;
  mixpanel.track(`${page} - ${action}`, {
    time: istTimestampSec(),
    action: page,
    button_name: action,
    event_category: 'interaction',
    environment: resolveEnvironment(),
    label,
    merchant_id: resolveMerchantId(),
  });
}

export function setMerchantSuperProps(merchant: {
  merchant_id?: string | number;
  store_id?: string | number;
  store_name?: string;
  shop?: string;
  platform?: string;
  currency?: string;
  plan?: string;
}): void {
  const props: Record<string, unknown> = {};
  if (merchant.merchant_id) props.merchant_id = merchant.merchant_id;
  if (merchant.store_id) props.store_id = merchant.store_id;
  if (merchant.store_name) props.store_name = merchant.store_name;
  if (merchant.shop) props.shop = merchant.shop;
  if (merchant.platform) props.platform = merchant.platform;
  if (merchant.currency) props.currency = merchant.currency;
  if (merchant.plan) props.plan = merchant.plan;
  registerAnalyticsSuperProps(props);
}
